import React, { useRef, useLayoutEffect, useState, useMemo, useEffect } from 'react';
import { formatInTimeZone } from 'date-fns-tz';
import {
  TimelinePopupWrapper,
  TimelinePopupHeader,
  HeaderLeft,
  HeaderCenter,
  HeaderRight,
  HeaderLabel,
  TimezoneDisplay,
  FieldGroup,
  FieldLabel,
  DateTimeRow,
  ClockIcon,
  RepeatRow,
  RepeatIcon,
  DateLabel,
  TimezoneHeader,
  ArrowIcon,
  CardFooter
} from '../../styles/timeline/popup.styles'; 
import { useToast } from '../../contextManager/toast.context';
import { useUser } from '../../contextManager/user.context';
import useAuth from '../../hooks/auth.service';
import { updateAvailabilitySeries, getTimezoneOffset } from '../../utils/availability';
import { AvailabilitySlot, AvailabilityObject } from '../../types/schemas/abc'; // Our new type
import TimeDropdown from './TimeDropdown';
import RepeatDropdown from './RepeatDropdown';
import { BannerIconButton } from '../../styles/search/search.style';
import { XCircleFill } from 'react-bootstrap-icons';
import { ActionButton } from '../../styles/profile/profile.style';
import { useScale } from '../../pages/HeightScaler';

// Helper to generate a simple unique ID
const generateUniqueId = () => `series_${Date.now()}`;

const generateTimeOptions = (): string[] => {
  const times: string[] = [];
  for (let h = 0; h < 24; h++) {
    ['00', '30'].forEach(m => times.push(`${String(h).padStart(2, '0')}:${m}`));
  }
  return times;
};

const formatTime = (totalMinutes: number) => {
  let h = Math.floor(totalMinutes / 60);
  const m = totalMinutes % 60;
  if (h >= 24) h = h % 24; 
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
};

const getPreviousDayName = (currentDayIndex: number) : keyof AvailabilityObject => {
  const days = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'] as const;
  const prevIndex = (currentDayIndex + 6) % 7;
  return days[prevIndex];
}

export interface TimelinePopupProps {
  date: string | Date;
  anchorRect: DOMRect;
  onClose: () => void;
  onBlur?: () => void;
  onFocus?: (e: React.FocusEvent) => void;
  viewingTz?: string;
  homeTz?: string;
}

export const TimelinePopup: React.FC<TimelinePopupProps> = ({ date: initialDate, anchorRect, onClose, onBlur, onFocus, viewingTz, homeTz }) => {
  const popupRef = useRef<HTMLDivElement>(null);
  const { patchProfile } = useAuth();
  const { addToast } = useToast();
  const { userDetails } = useUser();
  const {scale} = useScale();

  const userTZ = userDetails?.timezone

  const [targetDate, setTargetDate] = useState<Date>(
    typeof initialDate === 'string' ? new Date(initialDate) : initialDate
  );

  // Mode determines if we are adding a new series or modifying an existing one.
  const [mode, setMode] = useState<'Add' | 'Modify'>('Add');
  const [activeSlot, setActiveSlot] = useState<AvailabilitySlot | null>(null);

  // --- Form State ---
  const [startTime, setStartTime] = useState<string>('');
  const [endTime, setEndTime] = useState<string>('');
  const [repeatInterval, setRepeatInterval] = useState<string>('Weekly');
  const [initialState, setInitialState] = useState({ start: '', end: '', repeat: '' });
  // 2. Add state to track if the form has been changed ("dirty")
  const [isDirty, setIsDirty] = useState(false);
  // --- Logic to find which slot was clicked ---
  useMemo(() => {
    // By adding 'as const', TypeScript knows this array contains specific literal types,
    // which are assignable to 'keyof AvailabilityObject'.
    const clickDate = typeof initialDate === 'string' ? new Date(initialDate) : initialDate;
    // Use home timezone to extract day/hour, avoiding DST issues with browser-local
    const effectiveTz = homeTz || Intl.DateTimeFormat().resolvedOptions().timeZone;
    const dayIndexSun = parseInt(formatInTimeZone(clickDate, effectiveTz, 'e'), 10) - 1; // 1=Sun..7=Sat → 0-6
    const clickDayIndex = dayIndexSun;

    const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'] as const;
    const currentDayName = dayNames[dayIndexSun];

    const clickHour = parseInt(formatInTimeZone(clickDate, effectiveTz, 'H'), 10);
    const clickMin = parseInt(formatInTimeZone(clickDate, effectiveTz, 'm'), 10);
    const timeInMinutes = clickHour * 60 + clickMin;

    const availableToday: AvailabilitySlot[] = userDetails?.availability?.[currentDayName] || [];

    let foundSlot = availableToday.find((slot) => {
      return timeInMinutes >= slot.start && timeInMinutes < slot.end;
    });

    let foundDate = clickDate;

    if (!foundSlot) {
      const prevDayName = getPreviousDayName(clickDayIndex);
      const availableYesterday = userDetails?.availability?.[prevDayName] || [];

      const timeAsYesterday = timeInMinutes + 1440; 
      
      foundSlot = availableYesterday.find((slot) => {
        return timeAsYesterday >= slot.start && timeAsYesterday < slot.end;
      });

      if (foundSlot) {
        const prevDate = new Date(clickDate);
        prevDate.setDate(clickDate.getDate() - 1);
        foundDate = prevDate;
      }

    }

    setTargetDate(foundDate)

    // const clickedSlot = availableToday.find(
    //   (slot) => timeInMinutes >= slot.start && timeInMinutes < slot.end
    // );

    if (foundSlot) {
      // --- MODIFY MODE ---
      setMode('Modify');
      setActiveSlot(foundSlot);

      const initialStart = formatTime(foundSlot.start);
      const initialEnd = formatTime(foundSlot.end);
      const initialRepeat = foundSlot.repeat;
      
      // 3. When entering Modify mode, populate both the current state and the initial state
      setStartTime(initialStart);
      setEndTime(initialEnd);
      setRepeatInterval(initialRepeat);
      setInitialState({ start: initialStart, end: initialEnd, repeat: initialRepeat });
      setIsDirty(false); // Reset dirty state each time a new slot is loaded
    } else {
      // --- ADD MODE ---
      setMode('Add');
      setActiveSlot(null);
      setTargetDate(clickDate);

      const defaultStart = `${String(clickHour).padStart(2, '0')}:${clickMin < 30 ? '00' : '30'}`;
      const endHour = (clickHour + 1) % 24;
      const defaultEnd = `${String(endHour).padStart(2, '0')}:${clickMin < 30 ? '00' : '30'}`;
      setStartTime(defaultStart);
      setEndTime(defaultEnd);
      setRepeatInterval('Weekly');
    }
  }, [initialDate, userDetails]);

  useEffect(() => {
    if (mode === 'Modify') {
      const hasChanged =
        startTime !== initialState.start ||
        endTime !== initialState.end ||
        repeatInterval !== initialState.repeat;
      setIsDirty(hasChanged);
    }
  }, [startTime, endTime, repeatInterval, initialState, mode]);

  const [position, setPosition] = useState<{ top: number; left: number }>({ top: 0, left: 0 });

  useLayoutEffect(() => {
  }, [anchorRect]);
  
  const handleSave = async () => {
    const currentAvailability = userDetails?.availability || {
      monday: [], tuesday: [], wednesday: [],
      thursday: [], friday: [], saturday: [], sunday: []
    };
    // if (!currentAvailability) return;
    
    // Convert form times to minutes
    const [startH, startM] = startTime.split(':').map(Number);
    let totalStartMinutes = startH * 60 + startM;
    const [endH, endM] = endTime.split(':').map(Number);
    let totalEndMinutes = endH * 60 + endM;

    if (totalEndMinutes <= totalStartMinutes) {
      totalEndMinutes += 1440;
    }

    // Convert back to home tz if viewing in a different timezone
    let saveDayOfWeek = targetDate.getDay();
    if (viewingTz && homeTz && viewingTz !== homeTz) {
      const offset = getTimezoneOffset(homeTz, viewingTz);
      totalStartMinutes += offset;
      totalEndMinutes += offset;
      const MINUTES_IN_DAY = 1440;
      if (totalStartMinutes >= MINUTES_IN_DAY) {
        totalStartMinutes -= MINUTES_IN_DAY;
        totalEndMinutes -= MINUTES_IN_DAY;
        saveDayOfWeek = (saveDayOfWeek + 1) % 7;
      } else if (totalStartMinutes < 0) {
        totalStartMinutes += MINUTES_IN_DAY;
        totalEndMinutes += MINUTES_IN_DAY;
        saveDayOfWeek = (saveDayOfWeek + 6) % 7;
      }
    }

    let tempAvailability = currentAvailability;

    // If we are modifying, first remove the old series
    if (mode === 'Modify' && activeSlot) {
      tempAvailability = updateAvailabilitySeries(currentAvailability, 'remove', {
        id: activeSlot.id,
        repeat: activeSlot.repeat,
        startTime: 0,
        endTime: 0,
      });
    }

    // Add the new or updated series
    const finalAvailability = updateAvailabilitySeries(tempAvailability, 'add', {
      id: activeSlot?.id || generateUniqueId(),
      startTime: totalStartMinutes,
      endTime: totalEndMinutes,
      dayOfWeek: saveDayOfWeek,
      repeat: repeatInterval as AvailabilitySlot['repeat'],
    });

    try {
      await patchProfile(
        { availability: finalAvailability },
        { loading: 'Updating availability...', success: 'Availability updated.' },
      );
      onClose();
    } catch (err) {
      // Error toast handled inside patchProfile
    }
  };

  const handleRemove = async () => {
    if (mode !== 'Modify' || !activeSlot) return;

    const currentAvailability = userDetails?.availability;
    if (!currentAvailability) return;

    const finalAvailability = updateAvailabilitySeries(currentAvailability, 'remove', {
        id: activeSlot.id,
        repeat: activeSlot.repeat,
        startTime: 0, // Not needed for remove
        endTime: 0,   // Not needed for remove
    });

    try {
      await patchProfile(
        { availability: finalAvailability },
        { loading: 'Removing availability...', success: 'Availability series removed.' },
      );
      onClose();
    } catch (err) {
      // Error toast handled inside patchProfile
    }
  };


  useLayoutEffect(() => {
    if (!popupRef.current) return
    const GAP = 4
    const { innerWidth, innerHeight } = window
    const { offsetWidth: w, offsetHeight: h } = popupRef.current
    const scaledInnerWidth = innerWidth / scale
    const scaledInnerHeight = innerHeight / scale

    const scaledAnchorLeft = anchorRect.left / scale
    const scaledAnchorTop = anchorRect.top / scale
    let left = scaledAnchorLeft + GAP
    let top = scaledAnchorTop + GAP
    if (left + w + GAP > scaledInnerWidth) left = Math.max(GAP, scaledInnerWidth - w - GAP)
    if (top + h + GAP > scaledInnerHeight) top = scaledAnchorTop - h - GAP
    setPosition({ top, left })
  }, [anchorRect, scale])

  const timeOptions = useMemo(generateTimeOptions, []);

  return (
    <TimelinePopupWrapper 
      ref={popupRef} 
      style={{ top: position.top, left: position.left }}
      onFocus={onFocus} 
      onBlur={(e) => {
         // Safety check: Only turn off keyboard mode if we clicked OUTSIDE the popup
         // (prevents flickering when tabbing between Start Time and End Time)
         if (!e.currentTarget.contains(e.relatedTarget)) {
            onBlur && onBlur();
         }
      }}
      >
      <TimelinePopupHeader>
        <HeaderLeft>
          <HeaderLabel>{mode === 'Modify' ? 'Modify Availability' : 'Add Availability'}</HeaderLabel>
        </HeaderLeft>
        <HeaderCenter>
          <TimezoneHeader 
            css={{'@media (max-width: 600px)': {
              display: 'none',
            }}}
            >
            Timezone: 
            </TimezoneHeader>
          <TimezoneDisplay
              css={{'@media (max-width: 600px)': {
                display: 'none',
              }}}
            >
            {userTZ}
          </TimezoneDisplay>
        </HeaderCenter>
        <HeaderRight>
        <BannerIconButton onClick={() => {
      onClose();     
    }
  } >
         <XCircleFill size={18}/>       
        </BannerIconButton>
        </HeaderRight>
      </TimelinePopupHeader>

      <FieldGroup>
        <DateTimeRow css={{
          '@media (max-width: 600px)': {
              gap: 0,
            }
        }}>
          <ClockIcon />
          <DateLabel>{targetDate.toLocaleDateString('en-CA')}</DateLabel>
          <TimeDropdown value={startTime} options={timeOptions} onChange={setStartTime} />
          <FieldLabel><ArrowIcon size={15}/></FieldLabel>
          <TimeDropdown value={endTime} options={timeOptions} onChange={setEndTime} />
        </DateTimeRow>
        
        <RepeatRow css={{
          '@media (max-width: 600px)': {
            gap: 0,
          }
        }}>
          <RepeatIcon />
          <RepeatDropdown
            value={repeatInterval}
            onChange={setRepeatInterval}
            freeDropdown={true} // Use the version of the dropdown with only availability options
          />
        </RepeatRow>
      </FieldGroup>


      
      <CardFooter css={{marginTop: '1rem'}}>
      {mode === 'Modify' ? (     
        <>
        <ActionButton type="button" intent="primary" onClick={handleSave} disabled={!isDirty}>
          Save Changes
        </ActionButton>
        <ActionButton
          type="button"
          intent="danger"
          onClick={handleRemove}
        >
            Remove Series
            </ActionButton>
        </>
        ) 
        
        : (
          <>
        <ActionButton type="button" intent="secondary" onClick={onClose}>
          Cancel
        </ActionButton>
        <ActionButton
          type="button"
          intent="primary"
          onClick={handleSave}
        >
           Save and Close
        </ActionButton>
          </>
        )}
      </CardFooter>
    </TimelinePopupWrapper>
  );
};